// codelist-registry.js
//
// Read-only referentie-registry voor de admin-codelijstenpagina. Volledig AFGELEID uit
// @mv/models (de single source of truth). Per "domein" (dropdown) tonen we:
//   • welke waarden erin zitten,
//   • in welke NEN-standaard we ze tonen — DATA-GEDREVEN: de NEN 6693-set komt uit de
//     nen6693-codelijsten, de NEN 5104-vlag uit de bijbehorende `tiCodelists`-codelijst,
//   • naar welke TI/SIKB-code ze bij export vertalen (rood = geen schone TI-code).
//
// Zo verandert de pagina automatisch mee met de codelijsten en zijn de standaard-vlaggen
// niet geraden maar afgeleid uit de codelijst die elke standaard daadwerkelijk gebruikt.

import {
  nen6693 as N, tiCodelists as T,
  REFERENCE_PLANES, BORING_TYPE, BORING_TYPE_INSPECTIEGAT,
  WATERMONSTER_OPBRENGST, WATERMONSTER_POMPTYPE, WATERMONSTER_POMPTYPE_TI, WATERMONSTER_HELDERHEID,
  WATERMONSTER_MATRIX, WATERMONSTER_MATRIX_TI, MONSTER_VERPAKKING,
} from '@mv/models';

const BOTH = ['nen5104', 'nen6693'];
const ONLY_6693 = ['nen6693'];
const ONLY_5104 = ['nen5104'];

// Normaliseer voor term-matching tussen de nen6693- en tiCodelists-codelijsten
// (kleine letters, trim, diakrieten weg → 'crème' == 'creme').
const norm = (s) => (s ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const keySet = (map) => new Set([...map.keys()].map(norm));

/** TI-status: gemapt / ontbreekt (rood) / niet van toepassing (grijs, concept zonder TI-veld). */
function tiStatus(tiCode, applicable) {
  if (!applicable) return 'na';
  return tiCode ? 'mapped' : 'missing';
}
function value(label, code, standards, tiCode, { definition = '', applicable = true } = {}) {
  return { label, code: code ?? null, standards, tiCode: tiCode ?? null, tiStatus: tiStatus(tiCode, applicable), definition };
}
/** Standaarden voor een 6693-waarde: altijd 6693, plus 5104 als de term in de 5104-codelijst staat. */
const stds = (in5104) => (in5104 ? BOTH : ONLY_6693);

// ─────────────────────────────────────────────────────────────────────────────

const SOIL_5104 = keySet(T.bodemsoort);
function buildHoofdbestanddeel() {
  const shown = new Set();
  const groups = N.HOOFDBESTANDDEEL_GROEPEN.map((g) => ({
    label: g.label,
    values: g.items.map((it) => {
      shown.add(norm(it.term));
      const code = N.GRONDSOORT.get(it.term) || N.LAAGTYPE_SPECIAAL.get(it.term) || null;
      const isRots = it.term === 'rots';
      const tiCode = isRots ? null : N.soilTypeToTi(code);
      return value(it.label, code, stds(SOIL_5104.has(norm(it.term))), tiCode, {
        applicable: !isRots, definition: isRots ? 'TI-code hangt af van het gekozen gesteentetype' : '',
      });
    }),
  }));
  // NEN 5104-grondsoorten die NIET in de NEN 6693-keuzelijst staan (leem/water).
  const extras = [...T.bodemsoort.entries()].filter(([term]) => !shown.has(norm(term)))
    .map(([term, code]) => value(`${term} (${code})`, code, ONLY_5104, code));
  if (extras.length) groups.push({ label: 'Alleen NEN 5104', values: extras });
  return { id: 'hoofdbestanddeel', name: 'Hoofdbestanddeel', description: 'Grond-/steensoort en bijzondere laagtypen', groups };
}

const ADMIX_5104 = keySet(T.toevoeging);
function buildBijmenging() {
  const values = [...N.BIJMENGING.entries()].map(([term, code]) =>
    value(term, code, stds(ADMIX_5104.has(norm(term))), N.admixtureToTi(code)));
  const shown = new Set([...N.BIJMENGING.keys()].map(norm));
  for (const [term, code] of T.toevoeging)
    if (!shown.has(norm(term))) values.push(value(term, code, ONLY_5104, code));
  return { id: 'bijmenging', name: 'Bijmenging', description: 'Bijmengingen/toevoegingen (Tabel 9)', groups: [{ label: null, values }] };
}

// NEN 5104 kent voor de mediaan 6 klassen (M50), die gelden voor ZAND, GRIND én SLIB
// (MEDIAAN_SOIL_NAMES). NEN 6693 gebruikt 3 klassen, alleen voor zand en grind.
const MEDIAAN_5104 = [
  { term: 'uiterst fijn', code: '1', description: 'zeer fijn zand — 63 tot 105 µm' },
  { term: 'zeer fijn', code: '2', description: '105 tot 150 µm' },
  { term: 'matig fijn', code: '3', description: '150 tot 210 µm' },
  { term: 'matig grof', code: '4', description: '210 tot 300 µm' },
  { term: 'zeer grof', code: '5', description: '300 tot 420 µm' },
  { term: 'uiterst grof', code: '6', description: '420 µm tot 2,0 mm' },
];
function buildMediaan() {
  const grp6693 = (soilCode, label) => ({
    label,
    values: (N.MEDIAAN_KORRELGROOTTE[soilCode] || []).map(({ term, description }) =>
      value(term, N.mediaanDigit(soilCode, term) || term, ONLY_6693, N.medianToTi(soilCode, term), { definition: description })),
  });
  return {
    id: 'mediaan', name: 'Mediaan (korrelgrootte)',
    description: 'Mediane korrelklasse. NEN 6693: 3 klassen voor zand en grind. NEN 5104: 6 klassen (M50), voor zand, grind én slib.',
    groups: [
      grp6693('Z', 'Zand — NEN 6693 (3 klassen)'),
      grp6693('Gr', 'Grind — NEN 6693 (3 klassen)'),
      { label: 'NEN 5104 (6 klassen — zand/grind/slib)', values: MEDIAAN_5104.map(({ term, code, description }) => value(term, code, ONLY_5104, code, { definition: description })) },
    ],
  };
}

const COLOUR_5104 = keySet(T.kleur);
function buildKleur() {
  const values = [...N.KLEUR.entries()].map(([term, code]) =>
    value(term, code, stds(COLOUR_5104.has(norm(term))), N.colourToTi(code)));
  const shown = new Set([...N.KLEUR.keys()].map(norm));
  for (const [term, code] of T.kleur)
    if (!shown.has(norm(term))) values.push(value(term, code, ONLY_5104, code));
  return { id: 'kleur', name: 'Kleur', description: 'Hoofd- en bijkleur', groups: [{ label: null, values }] };
}

const SHADE_5104 = keySet(T.kleurSterkte);
function buildTint() {
  const excluded = [...N.KLEUR].filter(([, c]) => N.KLEUR_TINT_UITGESLOTEN.has(c)).map(([t]) => t).join(', ');
  const values = [...N.KLEUR_TINT.entries()].map(([term, code]) =>
    value(term || 'standaard', code === '' ? '(geen)' : code, stds(SHADE_5104.has(norm(term))), N.KLEUR_TINT_TI[code]));
  const shown = new Set([...N.KLEUR_TINT.keys()].map(norm));
  for (const [term, code] of T.kleurSterkte)
    if (!shown.has(norm(term))) values.push(value(term, code, ONLY_5104, code));
  return { id: 'tint', name: 'Tint (kleurgradatie)', description: `Licht / standaard / donker. Niet van toepassing bij: ${excluded}.`, groups: [{ label: null, values }] };
}

function consistentieValues(items) {
  return items.map(({ term, description }) => value(term, null, ONLY_6693, null, { definition: description, applicable: false }));
}
function buildConsistentie() {
  const groups = [
    { label: 'Minerale grondsoorten (klei/silt) — Tabel 24', values: consistentieValues(N.MINERALE_CONSISTENTIE) },
    { label: 'Organische grondsoorten — Tabel 23', values: consistentieValues(N.ORGANISCHE_CONSISTENTIE) },
    ...N.SLIB_CONSISTENTIE_GROEPEN.map((g) => ({ label: `Slib — ${g.label}`, values: consistentieValues(g.items) })),
  ];
  return { id: 'consistentie', name: 'Consistentie', description: 'Consistentie/rijping van een laag, per grondsoortgroep', groups };
}

const BZB_5104 = keySet(T.LaSpecialCompounds);
function buildBzb() {
  const bijzonder = N.BZB_GROEPEN.find((g) => /bestanddel/i.test(g.label))?.items || [];
  const gesteenten = N.BZB_GROEPEN.find((g) => /gesteent/i.test(g.label))?.items || [];
  const groups = [
    { label: 'Bijzondere bestanddelen', values: bijzonder.map((term) => {
      const code = N.BZB_SOORT.get(term) || null;
      return value(term, code, stds(BZB_5104.has(norm(term))), N.bzbSoortToTi(code)); }) },
    { label: 'Gesteenten', values: gesteenten.map((term) => {
      const code = N.GESTEENTE.get(term) || null;
      return value(term, code, stds(BZB_5104.has(norm(term))), N.bzbSoortToTi(code), {
        definition: N.isGesteenteBzbFallback(code) ? "Gemapt op 'bewerkt steen' (BS) — TerraIndex kent deze BZB niet" : '' }); }) },
  ];
  // NEN 5104-BZB's die NIET in de NEN 6693-keuzelijst staan. De asfalt-subtypes vallen onder de
  // aparte 'asfalt'-codelijst en de gesteenten hierboven, dus die tellen we mee als "wel getoond".
  const shown = new Set([...bijzonder, ...gesteenten, ...N.ASFALT_TYPEN].map(norm));
  // Idem de ballastfracties: die staan in NEN 6693 onder 'ballast' + de fractie-codelijst. Ze
  // heten in de TI-lijst anders dan in onze fractie-lijst ('ballast fractie 30/63mm' vs
  // '30/63 mm'), dus matchen we ze op TI-code in plaats van op term.
  const ballastTi = new Set(Object.values(N.BALLAST_FRACTIE_TI));
  const extras = [...T.LaSpecialCompounds.entries()]
    .filter(([term, code]) => !shown.has(norm(term)) && !ballastTi.has(code))
    .map(([term, code]) => value(`${term} (${code})`, code, ONLY_5104, code));
  if (extras.length) groups.push({ label: 'Alleen NEN 5104', values: extras });
  return {
    id: 'bzb', name: 'BZB (bijzonder bestanddeel)', description: 'Bijzondere bestanddelen en gesteenten in een laag (SIKB-codelijst)',
    groups,
  };
}

// TI LaSoilType-code → label, voor de gesteenten die op een naaste verwant terugvallen.
const TI_SOIL_LABEL = { '45': 'Gneis', '47': 'Leisteen', '35': 'Graniet', '42': 'Gabbro', T: 'Stenen' };
function buildGesteente() {
  // Rots als hoofdgrondsoort — NEN 5104 classificeert vast gesteente niet → NEN 6693-only.
  return {
    id: 'gesteente', name: 'Gesteente', description: 'Gesteentetypen bij hoofdbestanddeel "rots / vast gesteente" (Bijlage C.2)',
    groups: [{ label: null, values: [...N.GESTEENTE.entries()].map(([term, code]) => {
      const ti = N.GESTEENTE_TI[code];
      const approx = N.GESTEENTE_TI_APPROX.has(code);
      return value(term, code, ONLY_6693, ti, {
        definition: approx ? `TerraIndex kent dit gesteente niet exact → gemapt op naaste verwant ${TI_SOIL_LABEL[ti] || ti}` : '',
      });
    }) }],
  };
}

function buildAsfalt() {
  return {
    id: 'asfalt', name: 'Asfalt-type', description: 'Asfaltsoort bij een asfalt-BZB (TerraIndex heeft elke soort als losse BZB)',
    groups: [{ label: null, values: N.ASFALT_TYPEN.map((t) => value(t, null, stds(BZB_5104.has(norm(t))), N.asfaltTypeToTi(t))) }],
  };
}

function buildBallast() {
  // De fracties bestaan in NEN 5104 ook, maar daar als losse BZB's; hier zijn ze de
  // vervolgkeuze onder één 'ballast'. Vandaar de match op TI-code.
  const codes5104 = new Set(T.LaSpecialCompounds.values());
  return {
    id: 'ballast', name: 'Ballastfractie',
    description: 'Fractie (korrelmaat) bij een ballast-BZB (TerraIndex heeft elke fractie als losse BZB)',
    groups: [{ label: null, values: N.BALLAST_FRACTIES.map((t) => {
      const ti = N.ballastFractieToTi(t);
      return value(t, null, stds(codes5104.has(ti)), ti);
    }) }],
  };
}

const ORIGIN_5104 = keySet(T.LaSpecialCompoundsOrigin);
function buildHerkomst() {
  return {
    id: 'herkomst', name: 'Herkomst (BZB)', description: 'Antropogeen of gebiedseigen',
    groups: [{ label: null, values: [...N.BZB_HERKOMST.keys()].map((term) => value(term, term, stds(ORIGIN_5104.has(norm(term))), N.bzbHerkomstToTi(term))) }],
  };
}

function buildVolume() {
  // Beide standaarden kennen een BZB-volume/gradatie-veld met verschillende term-sets:
  // NEN 6693 = volume-% klassen (Tabel 35); NEN 5104 = SIKB-gradatie (LaSpecialCompoundsGradiation).
  const g5104 = T.LaSpecialCompoundsGradiation;
  const std = (in5104, in6693) => [in5104 && 'nen5104', in6693 && 'nen6693'].filter(Boolean);
  const g5104Keys = keySet(g5104);
  const values = [];
  for (const term of N.BZB_VOLUME.keys())
    values.push(value(term, term, std(g5104Keys.has(norm(term)), true), N.bzbVolumeToTi(term)));
  const shown = new Set([...N.BZB_VOLUME.keys()].map(norm));
  for (const [term, code] of g5104) if (!shown.has(norm(term))) values.push(value(term, term, ONLY_5104, code));
  return {
    id: 'volume', name: 'Volumeaandeel / gradatie (BZB)',
    description: 'Hoeveelheid van een bijzonder bestanddeel. NEN 6693 gebruikt volume-% klassen (Tabel 35), NEN 5104 de SIKB-gradatietermen — beide standaarden kennen dit veld.',
    groups: [{ label: null, values }],
  };
}

function buildBoorsysteem() {
  // Boorsysteem/boormethode van een boring — standaard-onafhankelijk (beide NEN-standaarden).
  return {
    id: 'boorsysteem', name: 'Boorsysteem', description: 'Boormethode van een boring → TI LaDrillingSystemTypeCode (SIKB Bodembemonstering, beide NEN-standaarden)',
    groups: [{ label: null, values: N.BOORSYSTEEM.map((b) => value(b.label, b.code, BOTH, b.ti, { definition: N.boorsysteemTiNote(b.code) })) }],
  };
}

function buildMaaiveldtype() {
  return {
    id: 'maaiveldtype', name: 'Maaiveldtype', description: 'Maaiveldtype van een boring → TI MpGroundLevelTypeCode (beide NEN-standaarden)',
    groups: [{ label: null, values: N.MAAIVELDTYPE.map((m) => value(m.label, m.code, BOTH, m.ti)) }],
  };
}

function buildGeur() {
  // Geur (type + gradatie) op een grondlaag — TerraIndex-logica, beide NEN-standaarden.
  return {
    id: 'geur', name: 'Geur', description: 'Geur-type en -gradatie van een laag → TI LaOdourTypeCode / LaOdourIntensityCode',
    groups: [
      { label: 'Geur-type', values: [...T.geurType.entries()].map(([label, code]) => value(label, code, BOTH, code)) },
      { label: 'Geur-gradatie', values: [...T.geurSterkte.entries()].map(([label, code]) => value(label, code, BOTH, code)) },
    ],
  };
}

function buildBoringType() {
  // Type onderzoek van een BORING (niet van een project — dat is 'werksoort'). Standaard-
  // onafhankelijk: TerraIndex kent één verzamelcodelijst voor het meetpunttype en NEN 6693
  // schrijft er geen eigen lijst voor.
  const notes = {
    HB: 'TerraIndex kent handboring niet als punttype — dat is een boormethode. Gaat als gewone boring (B); leg de methode vast bij Boorsysteem.',
    PU: 'TerraIndex kent pulsboring niet als punttype — dat is een boormethode. Gaat als gewone boring (B); leg de methode vast bij Boorsysteem.',
    ED: 'TerraIndex kent edelmanboring niet als punttype — dat is een boormethode. Gaat als gewone boring (B); leg de methode vast bij Boorsysteem.',
    [BORING_TYPE_INSPECTIEGAT]: 'Bij dit type hoort een uitgegraven sleuf: sleuflengte en -breedte (→ TI MpTrialPitLength/MpTrialPitWidth) en de zeefmassa\'s boven en onder 20 mm.',
  };
  return {
    id: 'boringtype', name: 'Type onderzoek (boring)',
    description: 'Wat voor gat er gemaakt is → TI MpTypeCode (beide NEN-standaarden). Niet te verwarren met de werksoort van een project.',
    groups: [{ label: null, values: BORING_TYPE.map((t) => value(t.label, t.code, BOTH, t.ti, { definition: notes[t.code] || '' })) }],
  };
}

function buildStopcriterium() {
  // Reden boring gestopt — bestaand boring-veld (MpStopCriterionCode), beide standaarden.
  return {
    id: 'stopcriterium', name: 'Reden boring gestopt', description: 'Stopcriterium van een boring → TI MpStopCriterionCode (beide NEN-standaarden)',
    groups: [{ label: null, values: [...T.stopCriterium.entries()].map(([label, code]) => value(label, code, BOTH, code)) }],
  };
}

function buildVulmateriaal() {
  // Afwerking blok 1 — vulmateriaal per traject. Vertaalt naar TerraIndex FiTypeCode (codelijst 28).
  const notes = {
    VUL_BK: "TI 'bentoniet' (BE).",
    VUL_BP: "TI 'bentoniet stof' (46).",
    VUL_BS: "TI 'grout' (GR).",
    VUL_OP: "Geen TI-vulmateriaal; 'anders' (AN) met toelichting in de opmerking.",
  };
  return {
    id: 'vulmateriaal', name: 'Vulmateriaal (afwerking)', description: 'Materiaal per vultraject bij het dichten van het boorgat (tab Afwerking, blok 1). Beide NEN-standaarden.',
    groups: [{ label: null, values: N.VULMATERIAAL.map((v) => value(v.label, v.code, BOTH, N.vulmateriaalTi(v.code), { definition: notes[v.code] || '' })) }],
  };
}

function buildBovenafwerking() {
  // Afwerking blok 3 — bovenafwerking bij een peilbuis. Staat niet in NEN 6693 (SIKB/BRO), maar
  // TerraIndex kent de bovenafwerking wél: vulling/bovenafwerking/casing zitten in TI in één
  // FiTypeCode-lijst, dus een vloerpot/straatpot/putdeksel mapt gewoon naar een TI-code.
  return {
    id: 'bovenafwerking', name: 'Bovenafwerking', description: 'Bovenafwerking bij een peilbuis (tab Afwerking, blok 3). Elke keuze combineert type én materiaal en mapt 1-op-1 op een TerraIndex-code; niet in NEN 6693 (SIKB/BRO).',
    groups: [
      { label: null, values: N.BOVENAFWERKING.map((b) => value(b.label, b.code, BOTH, N.bovenafwerkingTi(b.code))) },
    ],
  };
}

// ── Project ──────────────────────────────────────────────────────────────────

function buildWerksoort() {
  // Type onderzoek op (deel)projectniveau. Stuurt de NEN 6693-regelset (welke velden
  // verplicht zijn), maar TerraIndex kent geen veld voor de werksoort: het TI-projectrecord
  // heeft alleen PrCode/PrName. Er valt dus niets te mappen — bewust 'niet van toepassing'
  // i.p.v. rood, want er ontbreekt geen mapping, het veld bestaat simpelweg niet in TI.
  return {
    id: 'werksoort', name: 'Type onderzoek (project)',
    description: 'Type onderzoek van een (deel)project. Bepaalt welke NEN 6693-regelset geldt en dus welke velden verplicht zijn.',
    groups: [{
      label: null,
      values: N.WERKSOORT.map((w) => value(w.label, w.code, ONLY_6693, null, {
        applicable: false, definition: w.short,
      })),
    }],
  };
}

// ── Boring ───────────────────────────────────────────────────────────────────

function buildReferentievlak() {
  // Referentievlak van een boring: waar de dieptes vandaan geteld worden.
  // LET OP: de TI-export schrijft MpReferencePointTypeCode op dit moment uit het
  // HOOGTEDATUM van het project (NAP → 'NA'), niet uit dit veld. De tiCode hieronder is
  // dus wel gedefinieerd maar nog niet geverifieerd tegen TI (tiCodeVerified: false).
  const G = REFERENCE_PLANES;
  return {
    id: 'referentievlak', name: 'Referentievlak (boring)',
    description: 'Vlak waar de laagdieptes van een boring vanaf geteld worden.',
    groups: [{
      label: null,
      values: Object.values(G).map((r) => value(r.label, r.id, BOTH, null, {
        applicable: false,
        definition: `Beoogde TI-code '${r.tiCode}'${r.tiCodeVerified ? '' : ' (nog niet geverifieerd)'} — nu niet geëxporteerd.`,
      })),
    }],
  };
}

// ── Laag ─────────────────────────────────────────────────────────────────────

function buildOlieWater() {
  // Reactie → TI LaOWTypeCode (numeriek, wordt geëxporteerd).
  // Overige waarneming → NEN 6693-concept (kleur/effect van de drijflaag); TerraIndex
  // heeft hier geen veld voor, dus niet exporteerbaar.
  return {
    id: 'olie-water', name: 'Olie/water-reactie',
    description: 'Reactie van een laag op de olie/water-test, plus de aanvullende waarneming van de drijflaag (kleur of effect).',
    groups: [
      {
        label: 'Reactie (sterkte)',
        values: [...T.olieWaterReactie.entries()].map(([label, code]) => value(label, code, BOTH, code)),
      },
      {
        label: 'Overige waarneming olie-watertest',
        values: N.OLIE_WATER_ANDERE_WAARNEMING.map((o) => value(o.term, o.term, ONLY_6693, null, {
          applicable: false, definition: o.description,
        })),
      },
    ],
  };
}

// ── Peilbuis ─────────────────────────────────────────────────────────────────

function buildPeilbuisCodes() {
  // Alle peilbuis-dropdowns bij elkaar; alle drie mappen 1-op-1 op een TI-veld.
  return {
    id: 'peilbuis', name: 'Peilbuis',
    description: 'Referentievlak van de filterdiepte, buismateriaal en doorlatendheid van een peilbuis.',
    groups: [
      { label: 'Referentievlak (filterdiepte)', values: [...T.peilbuisReferentievlak.entries()].map(([label, code]) => value(label, code, BOTH, code)) },
      { label: 'Materiaal', values: [...T.peilbuisMateriaal.entries()].map(([label, code]) => value(label, code, BOTH, code)) },
      { label: 'Doorloop', values: [...T.peilbuisDoorloop.entries()].map(([label, code]) => value(label, code, BOTH, code)) },
    ],
  };
}

// ── Watermonster ─────────────────────────────────────────────────────────────

function buildWatermonster() {
  // Opbrengst, pomptype en transparantie zijn eigen GeoVoice-keuzelijsten. TerraIndex kent
  // hiervoor codelijsten (WsYieldCode / WsPumpTypeCode / WsClarityCode). Het pomptype gaat mee
  // via WATERMONSTER_POMPTYPE_TI (codelijst 165); opbrengst en transparantie schrijft de export nog niet.
  const pumpType = (o) => {
    const tiCode = WATERMONSTER_POMPTYPE_TI.get(o);
    const definition = tiCode
      ? (o === 'slangenpomp (peristaltisch)' ? 'Heet in TerraIndex "peristaltische pomp".' : '')
      : 'Geen TI-tegenhanger in codelijst 165; WsPumpTypeCode blijft leeg bij export.';
    return value(o, o, BOTH, tiCode, { definition });
  };
  const notExported = (tiField) => ({
    applicable: false,
    definition: `TerraIndex kent hiervoor ${tiField}, maar onze export vult dat veld nog niet.`,
  });
  return {
    id: 'watermonster', name: 'Watermonster',
    description: 'Matrix, opbrengst van de peilbuis, gebruikt pomptype en de transparantie van het opgepompte water.',
    groups: [
      {
        label: 'Matrix',
        values: WATERMONSTER_MATRIX.map((o) => {
          const ti = WATERMONSTER_MATRIX_TI.get(o);
          return value(o, o, BOTH, ti, {
            definition: ti
              ? `→ TI WsMatrixCode "${ti}".`
              : 'TI-code nog niet vastgesteld — WsMatrixCode blijft bij export leeg.',
          });
        }),
      },
      { label: 'Opbrengst', values: WATERMONSTER_OPBRENGST.map((o) => value(o, o, BOTH, null, notExported('WsYieldCode'))) },
      { label: 'Pomptype', values: WATERMONSTER_POMPTYPE.map(pumpType) },
      { label: 'Transparantie', values: WATERMONSTER_HELDERHEID.map((o) => value(o, o, ONLY_6693, null, notExported('WsClarityCode'))) },
    ],
  };
}

// ── Grondmonster ─────────────────────────────────────────────────────────────

function buildVerpakking() {
  // Gecureerde selectie uit de 96 TI-opties (JrPackingTypeCode): wat in het Nederlandse
  // veldwerk daadwerkelijk gebruikt wordt. Codes zijn 1-op-1 TI-codes.
  const groups = [];
  for (const v of MONSTER_VERPAKKING) {
    if (!groups.length || groups[groups.length - 1].label !== v.group) groups.push({ label: v.group, values: [] });
    groups[groups.length - 1].values.push(value(v.label, v.code, BOTH, v.code));
  }
  return {
    id: 'verpakking', name: 'Verpakking (grondmonster)',
    description: 'Type verpakking van een grondmonster — pot, fles, emmer, steekbus enzovoort.',
    groups,
  };
}

// ── Afwerking ────────────────────────────────────────────────────────────────

function buildAchtergelaten() {
  // Objecten die in de grond achterblijven (blok 2 van de afwerking) → TI FiTypeCode,
  // samen met vullingen en bovenafwerking in tblFinishings.
  return {
    id: 'achtergelaten', name: 'Achtergelaten in de grond',
    description: 'Objecten die na het boren in de bodem achterblijven of er bewust in geplaatst worden (tab Afwerking).',
    groups: [{
      label: null,
      values: N.ACHTERGELATEN_TYPE.map((a) => value(a.label, a.code, BOTH, N.ACHTERGELATEN_TYPE_TI[a.code] || null, {
        definition: `Aard: ${a.aard}.${N.ACHTERGELATEN_TYPE_TI[a.code] === 'AN' ? " TI kent dit type niet — gaat als 'AN' (anders) met de toelichting in FiRemark." : ''}`,
      })),
    }],
  };
}

let _cache = null;
/** Alle domeinen (codelijsten) met hun waarden. Afgeleid uit @mv/models. */
export function getCodelistDomains() {
  if (_cache) return _cache;
  const domains = [
    buildHoofdbestanddeel(), buildBijmenging(), buildMediaan(), buildKleur(), buildTint(),
    buildConsistentie(), buildBzb(), buildGesteente(), buildAsfalt(), buildBallast(), buildHerkomst(), buildVolume(),
    buildBoorsysteem(), buildMaaiveldtype(), buildGeur(), buildStopcriterium(), buildBoringType(),
    buildVulmateriaal(), buildBovenafwerking(), buildAchtergelaten(),
    buildVerpakking(), buildWerksoort(), buildReferentievlak(), buildOlieWater(), buildPeilbuisCodes(), buildWatermonster(),
  ];
  for (const d of domains) {
    d.values = d.groups.flatMap((g) => g.values);
    d.count = d.values.length;
    d.standards = [...new Set(d.values.flatMap((v) => v.standards))].sort();
    d.missingCount = d.values.filter((v) => v.tiStatus === 'missing').length;
    // Waarden zonder TI-koppeling omdat TerraIndex het concept niet kent (geen fout).
    d.naCount = d.values.filter((v) => v.tiStatus === 'na').length;
  }
  _cache = domains;
  return domains;
}
